import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  CloseInstructionBody,
  InstructionValidityView,
  InstructionView,
  OpenInstructionBody,
  TaskView,
  ThresholdMode,
} from '@/services/manualTradingApi';
import {
  cancelInstruction,
  createCloseInstruction,
  createOpenInstruction,
  isManualTradingConfigured,
  listInFlightTasks,
  listInstructions,
  listInstructionsIfIdle,
  pauseInstruction,
  removeInFlightTask,
  resumeInstruction,
  submitSingleLegTask,
  taskKeyBodyFromTaskView,
  updateInstruction,
  type SubmitSingleLegTaskBody,
} from '@/services/manualTradingApi';
import {
  formatInstructionNotionalProgress,
  formatInstructionSizeProgress,
  instructionNotionalProgressTitle,
  instructionSizeProgressTitle,
  isInstructionProgressNa,
} from '@/lib/instructionDisplay';
import { buildInstructionExchangePairQuery, notifyInstructionsChanged } from '@/lib/instructionThresholdLines';
import { resolveManualTradingLegs } from '@/lib/manualTradingLegs';
import { confirmOpenThresholdIfNeeded } from '@/lib/openLineThresholdConfirm';
import { useAuth } from '@/auth/AuthContext';
import { labelToManualTradingExchange } from '@/services/manualTradingExchange';
import { InFlightTaskTable } from './InFlightTaskTable';
import './ManualTradingPanel.css';

type Props = {
  symbol: string;
  exchange1: string;
  exchange2: string;
  /** Native contract id on `exchange1` (e.g. OKX `BTC-USDT-SWAP`). */
  exchange1InstId: string;
  /** Native contract id on `exchange2` (e.g. Bybit `BTCUSDT`). */
  exchange2InstId: string;
  /** Corresponds to RealtimeSpread `config.direction`: long1_short2 | short1_long2 */
  realtimeDirection: 'long1_short2' | 'short1_long2';
  /** Latest open_line from spread/detailed for the current direction (raw ratio). */
  latestOpenLine?: number | null;
  /** Span from column top to chart bottom; tab body scrolls inside. */
  panelHeight?: number;
};

/** UI 关仓模式 → trading-engine `ThresholdMode` */
function closeThresholdMode(mode: 'Immediate' | 'SpreadThreshold'): CloseInstructionBody['threshold_mode'] {
  return mode === 'SpreadThreshold' ? 'LessOrEqual' : 'Immediate';
}

function formatThresholdMode(mode: string): string {
  switch (mode) {
    case 'Immediate':
      return '立即';
    case 'GreaterOrEqual':
      return '价差≥阈值';
    case 'LessOrEqual':
      return '价差≤阈值';
    default:
      return mode;
  }
}

function formatInstructionValidity(validity: InstructionValidityView): string {
  return validity === 'persistent' ? '永久' : '单次';
}

function isPersistentCloseInstruction(row: InstructionView): boolean {
  return row.action === 'Close' && row.validity === 'persistent';
}

function spreadDirectionShortLabel(direction: 'long1_short2' | 'short1_long2'): string {
  return direction === 'long1_short2' ? 'L1S2' : 'S1L2';
}

function spreadDirectionDetailLabel(
  direction: 'long1_short2' | 'short1_long2',
  exchange1: string,
  exchange2: string,
): string {
  return direction === 'long1_short2'
    ? `${exchange1} 多 / ${exchange2} 空`
    : `${exchange1} 空 / ${exchange2} 多`;
}

/** Compact exchange label for narrow instruction table cells. */
function shortExchangeLabel(exchange: string): string {
  const map: Record<string, string> = {
    OKX: 'OKX',
    Bybit: 'BB',
    Binance: 'BN',
    Gateio: 'GT',
    Bitget: 'BG',
    Hyperliquid: 'HL',
    Lighter: 'LT',
    Aster: 'AS',
    EdgeX: 'EX',
    Paradex: 'PD',
  };
  return map[exchange] ?? exchange.slice(0, 4);
}

type InstructionActionFilter = 'all' | 'Open' | 'Close';

function groupInstructionsBySymbol(rows: InstructionView[]): [string, InstructionView[]][] {
  const groups = new Map<string, InstructionView[]>();
  for (const row of rows) {
    const list = groups.get(row.symbol);
    if (list) {
      list.push(row);
    } else {
      groups.set(row.symbol, [row]);
    }
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([sym, list]) => [
      sym,
      [...list].sort((a, b) => {
        if (a.action !== b.action) return a.action === 'Open' ? -1 : 1;
        return a.instruction_id.localeCompare(b.instruction_id);
      }),
    ]);
}

function InstructionLegDirection({ row }: { row: InstructionView }) {
  return (
    <span className="trading-engine-leg-dir">
      <span
        className="trading-engine-leg is-long"
        title={`Long ${row.long_exchange} · ${row.long_inst_id}`}
      >
        L&nbsp;{shortExchangeLabel(row.long_exchange)}
      </span>
      <span className="trading-engine-leg is-short" title={`Short ${row.short_exchange} · ${row.short_inst_id}`}>
        S&nbsp;{shortExchangeLabel(row.short_exchange)}
      </span>
    </span>
  );
}

function InstructionActionIcon({ variant }: { variant: 'pause' | 'resume' | 'edit' | 'cancel' }) {
  switch (variant) {
    case 'pause':
      return (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <rect x="3.5" y="2.5" width="3" height="11" rx="0.75" fill="currentColor" />
          <rect x="9.5" y="2.5" width="3" height="11" rx="0.75" fill="currentColor" />
        </svg>
      );
    case 'resume':
      return (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path d="M4.5 2.8c-.55-.32-1.25.12-1.25.78v9.84c0 .66.7 1.1 1.25.78l7.5-4.42c.52-.31.52-1.05 0-1.36L4.5 2.8Z" fill="currentColor" />
        </svg>
      );
    case 'edit':
      return (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path
            d="M11.1 2.3a1.1 1.1 0 0 1 1.55 0l1.05 1.05a1.1 1.1 0 0 1 0 1.55l-6.9 6.9-2.55.55.55-2.55 6.9-6.9Z"
            fill="currentColor"
          />
          <path d="M3 12.5h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      );
    case 'cancel':
      return (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path
            d="M4.2 4.2 11.8 11.8M11.8 4.2 4.2 11.8"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      );
  }
}

function InstructionActionIconButton({
  variant,
  label,
  disabled,
  busy,
  onClick,
}: {
  variant: 'pause' | 'resume' | 'edit' | 'cancel';
  label: string;
  disabled?: boolean;
  busy?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`trading-engine-instruction-icon-btn is-${variant}`}
      disabled={disabled}
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      {busy ? '…' : <InstructionActionIcon variant={variant} />}
    </button>
  );
}

type InstructionEditDraft = {
  targetNotional: string;
  perOrderNotional: string;
  thresholdMode: ThresholdMode;
  threshold: string;
  minIntervalMs: string;
};

function draftFromInstruction(row: InstructionView): InstructionEditDraft {
  const thresholdMode =
    row.action === 'Open' && row.threshold_mode === 'LessOrEqual'
      ? 'GreaterOrEqual'
      : row.threshold_mode;
  return {
    targetNotional: String(row.target_notional),
    perOrderNotional: String(row.per_order_notional),
    thresholdMode,
    threshold: row.threshold != null ? String(row.threshold) : '0',
    minIntervalMs: String(row.min_order_interval_ms),
  };
}

function InstructionEditForm({
  row,
  busy,
  onCancel,
  onSave,
}: {
  row: InstructionView;
  busy: boolean;
  onCancel: () => void;
  onSave: (draft: InstructionEditDraft) => void;
}) {
  const [draft, setDraft] = useState<InstructionEditDraft>(() => draftFromInstruction(row));
  const hideTargetNotional = isPersistentCloseInstruction(row);

  useEffect(() => {
    setDraft(draftFromInstruction(row));
  }, [row]);

  return (
    <div className="trading-engine-instruction-edit">
      <p className="trading-engine-instruction-edit-title">
        修改指令 · {row.symbol} · {row.action === 'Close' ? '关仓' : '开仓'}
        {hideTargetNotional ? ' · 永久' : ''}
        <span className="mono"> {row.instruction_id.slice(0, 8)}…</span>
      </p>
      {hideTargetNotional ? (
        <p className="hint">永久关仓指令不设目标名义，每次触发按单次名义执行，直至手动取消。</p>
      ) : null}
      <div className="trading-engine-instruction-edit-grid">
        {hideTargetNotional ? null : (
          <label>
            目标名义 target_notional
            <input
              type="number"
              step="any"
              value={draft.targetNotional}
              disabled={busy}
              onChange={(e) => setDraft((d) => ({ ...d, targetNotional: e.target.value }))}
            />
          </label>
        )}
        <label>
          单次名义 per_order_notional
          <input
            type="number"
            step="any"
            value={draft.perOrderNotional}
            disabled={busy}
            onChange={(e) => setDraft((d) => ({ ...d, perOrderNotional: e.target.value }))}
          />
        </label>
        <label>
          触发模式 threshold_mode
          <select
            value={draft.thresholdMode}
            disabled={busy}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                thresholdMode: e.target.value as ThresholdMode,
              }))
            }
          >
            {row.action === 'Open' ? (
              <>
                <option value="GreaterOrEqual">价差 ≥ 阈值</option>
                <option value="Immediate">立即</option>
              </>
            ) : (
              <>
                <option value="GreaterOrEqual">价差 ≥ 阈值</option>
                <option value="LessOrEqual">价差 ≤ 阈值</option>
                <option value="Immediate">立即</option>
              </>
            )}
          </select>
        </label>
        {draft.thresholdMode !== 'Immediate' ? (
          <label>
            价差阈值 threshold
            <input
              type="number"
              step="any"
              value={draft.threshold}
              disabled={busy}
              onChange={(e) => setDraft((d) => ({ ...d, threshold: e.target.value }))}
            />
          </label>
        ) : null}
        <label>
          下单间隔 ms
          <input
            type="number"
            min={0}
            step={1}
            value={draft.minIntervalMs}
            disabled={busy}
            onChange={(e) => setDraft((d) => ({ ...d, minIntervalMs: e.target.value }))}
          />
        </label>
      </div>
      <div className="trading-engine-instruction-edit-actions">
        <button type="button" className="btn-secondary btn-inline" disabled={busy} onClick={onCancel}>
          取消
        </button>
        <button
          type="button"
          className="btn-open btn-inline"
          disabled={busy}
          onClick={() => onSave(draft)}
        >
          {busy ? '保存中…' : '保存修改'}
        </button>
      </div>
    </div>
  );
}

export function ManualTradingPanel({
  symbol,
  exchange1,
  exchange2,
  exchange1InstId,
  exchange2InstId,
  realtimeDirection,
  latestOpenLine = null,
  panelHeight,
}: Props) {
  const { user, isTradingSessionReady } = useAuth();
  const [totalNotional, setTotalNotional] = useState('200');
  const [perOrderNotional, setPerOrderNotional] = useState('20');
  const [openMode, setOpenMode] = useState<OpenInstructionBody['threshold_mode']>('GreaterOrEqual');
  const [spreadThreshold, setSpreadThreshold] = useState('0.005');
  const [minIntervalMs, setMinIntervalMs] = useState('500');

  const [closeMode, setCloseMode] = useState<'Immediate' | 'SpreadThreshold'>('Immediate');
  const [closeSpreadThreshold, setCloseSpreadThreshold] = useState('0');
  const [closeValidity, setCloseValidity] = useState<InstructionValidityView>('one_shot');
  const [closeSizeMode, setCloseSizeMode] = useState<'token' | 'usd'>('usd');
  const [closeTargetAmount, setCloseTargetAmount] = useState('100');
  const [closePerOrderNotional, setClosePerOrderNotional] = useState('20');

  const [singleLegExchange, setSingleLegExchange] = useState<'1' | '2'>('1');
  const [singleLegSide, setSingleLegSide] = useState<'Long' | 'Short'>('Long');
  const [singleLegAction, setSingleLegAction] = useState<'Open' | 'Close'>('Open');
  const [singleLegTokenSize, setSingleLegTokenSize] = useState('0.01');
  const [singleLegSizeMode, setSingleLegSizeMode] = useState<'token' | 'usd'>('token');
  const [singleLegNotional, setSingleLegNotional] = useState('20');

  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [panelTab, setPanelTab] = useState<'trade' | 'instructions' | 'orders'>('trade');
  const [tradeSubTab, setTradeSubTab] = useState<'open' | 'close'>('open');
  const [legMode, setLegMode] = useState<'hedge' | 'singleLeg'>('hedge');
  const [inFlightTasks, setInFlightTasks] = useState<TaskView[]>([]);
  const [inFlightLoading, setInFlightLoading] = useState(false);
  const [inFlightError, setInFlightError] = useState<string | null>(null);
  const [instructions, setInstructions] = useState<InstructionView[]>([]);
  const [instructionsLoading, setInstructionsLoading] = useState(false);
  const [instructionsError, setInstructionsError] = useState<string | null>(null);
  const [instructionActionFilter, setInstructionActionFilter] = useState<InstructionActionFilter>('all');
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [pausingId, setPausingId] = useState<string | null>(null);
  const [resumingId, setResumingId] = useState<string | null>(null);
  const [deletingTaskId, setDeletingTaskId] = useState<string | null>(null);
  const [editingInstruction, setEditingInstruction] = useState<InstructionView | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const legs = useMemo(
    () => resolveManualTradingLegs(exchange1, exchange2, realtimeDirection),
    [exchange1, exchange2, realtimeDirection],
  );

  const instructionPairQuery = useMemo(
    () => buildInstructionExchangePairQuery(symbol, exchange1, exchange2),
    [symbol, exchange1, exchange2],
  );

  const pairContextLabel = useMemo(() => {
    if (!instructionPairQuery) return '';
    return `${instructionPairQuery.symbol} · ${instructionPairQuery.exchange1} / ${instructionPairQuery.exchange2}`;
  }, [instructionPairQuery]);

  const tradeContext = useMemo(() => {
    if (!legs.ok) return null;
    const longInst = (realtimeDirection === 'long1_short2' ? exchange1InstId : exchange2InstId).trim();
    const shortInst = (realtimeDirection === 'long1_short2' ? exchange2InstId : exchange1InstId).trim();
    return {
      symbol: symbol.trim() || '—',
      directionCode: spreadDirectionShortLabel(realtimeDirection),
      directionDetail: spreadDirectionDetailLabel(realtimeDirection, exchange1, exchange2),
      longExchange: legs.long_exchange,
      shortExchange: legs.short_exchange,
      longInst: longInst || '—',
      shortInst: shortInst || '—',
    };
  }, [
    legs,
    symbol,
    exchange1,
    exchange2,
    exchange1InstId,
    exchange2InstId,
    realtimeDirection,
  ]);

  const groupedInstructions = useMemo(
    () => groupInstructionsBySymbol(instructions),
    [instructions]
  );

  const clearFeedback = () => {
    setMessage(null);
    setError(null);
  };

  const onSubmitOpen = useCallback(async () => {
    clearFeedback();
    if (!isManualTradingConfigured()) {
      setError('请在 .env 中配置 VITE_MANUAL_TRADING_ORIGIN');
      return;
    }
    if (!legs.ok) {
      setError(legs.error);
      return;
    }
    if (!symbol.trim()) {
      setError('Symbol 为空');
      return;
    }
    const inst1 = exchange1InstId.trim();
    const inst2 = exchange2InstId.trim();
    if (!inst1 || !inst2) {
      setError('缺少交易所合约 inst_id（请确认币种与交易所在本页支持的 inst_id 映射内）');
      return;
    }
    const long_inst_id = realtimeDirection === 'long1_short2' ? inst1 : inst2;
    const short_inst_id = realtimeDirection === 'long1_short2' ? inst2 : inst1;

    const total = Number(totalNotional.trim());
    const perOrder = Number(perOrderNotional.trim());
    if (!(total > 0) || !Number.isFinite(total)) {
      setError('总名义须为有效正数');
      return;
    }
    if (!(perOrder > 0) || !Number.isFinite(perOrder)) {
      setError('单次开仓名义须为有效正数');
      return;
    }
    let threshold: number | null = null;
    if (openMode !== 'Immediate') {
      threshold = Number(spreadThreshold.trim());
      if (!Number.isFinite(threshold)) {
        setError('开仓价差阈值须为有效数字');
        return;
      }
      if (
        !confirmOpenThresholdIfNeeded({
          threshold,
          openLine: latestOpenLine,
          direction: realtimeDirection,
        })
      ) {
        return;
      }
    }

    const body: OpenInstructionBody = {
      symbol: symbol.trim(),
      long_inst_id,
      short_inst_id,
      long_exchange: legs.long_exchange,
      short_exchange: legs.short_exchange,
      target_notional: total,
      per_order_notional: perOrder,
      threshold_mode: openMode,
      threshold,
      min_order_interval_ms: Number(minIntervalMs) || 0,
    };

    setBusy(true);
    try {
      await createOpenInstruction(body);
      notifyInstructionsChanged();
      setMessage(`已提交开仓指令（${exchange1}/${exchange2} → long ${body.long_exchange} / short ${body.short_exchange}）`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [
    legs,
    symbol,
    totalNotional,
    perOrderNotional,
    openMode,
    spreadThreshold,
    minIntervalMs,
    exchange1,
    exchange2,
    exchange1InstId,
    exchange2InstId,
    realtimeDirection,
    latestOpenLine,
  ]);

  const onSubmitSingleLeg = useCallback(async () => {
    clearFeedback();
    if (!isManualTradingConfigured()) {
      setError('请在 .env 中配置 VITE_MANUAL_TRADING_ORIGIN');
      return;
    }
    if (!legs.ok) {
      setError(legs.error);
      return;
    }
    if (!symbol.trim()) {
      setError('Symbol 为空');
      return;
    }
    const inst1 = exchange1InstId.trim();
    const inst2 = exchange2InstId.trim();
    if (!inst1 || !inst2) {
      setError('缺少交易所合约 inst_id');
      return;
    }
    const useEx1 = singleLegExchange === '1';
    const exchange = useEx1
      ? labelToManualTradingExchange(exchange1)
      : labelToManualTradingExchange(exchange2);
    const inst_id = useEx1 ? inst1 : inst2;
    if (!exchange) {
      setError('无法识别所选交易所');
      return;
    }
    const useUsd = singleLegSizeMode === 'usd';
    const amount = Number((useUsd ? singleLegNotional : singleLegTokenSize).trim());
    if (!(amount > 0) || !Number.isFinite(amount)) {
      setError(useUsd ? 'USD 名义须为有效正数' : 'Token 数量须为有效正数');
      return;
    }

    const body: SubmitSingleLegTaskBody = {
      exchange,
      symbol: symbol.trim(),
      inst_id,
      action: singleLegAction,
      position_side: singleLegSide,
      ...(useUsd ? { notional: amount } : { token_size: amount }),
    };

    setBusy(true);
    try {
      const res = await submitSingleLegTask(body);
      setMessage(
        `已提交单腿 task（${singleLegAction} ${singleLegSide} · ${exchange} · ${inst_id} · task_id=${res.task_id}）`
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [
    legs,
    symbol,
    exchange1,
    exchange2,
    exchange1InstId,
    exchange2InstId,
    singleLegExchange,
    singleLegSide,
    singleLegAction,
    singleLegTokenSize,
    singleLegNotional,
    singleLegSizeMode,
  ]);

  const onSubmitClose = useCallback(async () => {
    clearFeedback();
    if (!isManualTradingConfigured()) {
      setError('请在 .env 中配置 VITE_MANUAL_TRADING_ORIGIN');
      return;
    }
    if (!legs.ok) {
      setError(legs.error);
      return;
    }
    const isPersistentClose = closeValidity === 'persistent';
    const perOrder = Number(closePerOrderNotional.trim());
    if (!(perOrder > 0)) {
      setError('单次关仓名义需为正数');
      return;
    }
    const targetAmount = Number(closeTargetAmount.trim());
    if (!isPersistentClose) {
      if (!(targetAmount > 0) || !Number.isFinite(targetAmount)) {
        setError(closeSizeMode === 'usd' ? '总关仓 USD 名义须为正数' : '总关仓 token 数量须为正数');
        return;
      }
    }
    const inst1 = exchange1InstId.trim();
    const inst2 = exchange2InstId.trim();
    if (!inst1 || !inst2) {
      setError('缺少交易所合约 inst_id（请确认币种与交易所在本页支持的 inst_id 映射内）');
      return;
    }
    const long_inst_id = realtimeDirection === 'long1_short2' ? inst1 : inst2;
    const short_inst_id = realtimeDirection === 'long1_short2' ? inst2 : inst1;

    let threshold: number | null = null;
    if (closeMode === 'SpreadThreshold') {
      threshold = Number(closeSpreadThreshold.trim());
      if (!Number.isFinite(threshold)) {
        setError('关仓价差阈值须为有效数字');
        return;
      }
    }

    const body: CloseInstructionBody = {
      symbol: symbol.trim(),
      long_inst_id,
      short_inst_id,
      long_exchange: legs.long_exchange,
      short_exchange: legs.short_exchange,
      ...(isPersistentClose
        ? {}
        : closeSizeMode === 'usd'
          ? { target_notional: targetAmount }
          : { target_size: targetAmount }),
      per_order_notional: perOrder,
      threshold_mode: closeThresholdMode(closeMode),
      threshold,
      min_order_interval_ms: Number(minIntervalMs) || 0,
      validity: closeValidity,
    };

    setBusy(true);
    try {
      await createCloseInstruction(body);
      notifyInstructionsChanged();
      setMessage(
        isPersistentClose
          ? `已提交永久关仓指令（单次 ${body.per_order_notional} USD，持续监听价差直至取消）。`
          : `已提交关仓指令（总目标 ${targetAmount} ${closeSizeMode === 'usd' ? 'USD' : 'token'} / 单次 ${body.per_order_notional} USD）。`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [
    legs,
    symbol,
    closeValidity,
    closeTargetAmount,
    closeSizeMode,
    closePerOrderNotional,
    closeMode,
    closeSpreadThreshold,
    minIntervalMs,
    exchange1InstId,
    exchange2InstId,
    realtimeDirection,
  ]);

  const refreshInFlightTasks = useCallback(async () => {
    if (!isManualTradingConfigured()) return;
    setInFlightLoading(true);
    setInFlightError(null);
    try {
      const rows = await listInFlightTasks();
      setInFlightTasks(rows);
    } catch (e) {
      setInFlightError(e instanceof Error ? e.message : String(e));
    } finally {
      setInFlightLoading(false);
    }
  }, []);

  const refreshInstructions = useCallback(async (force = false) => {
    if (!isManualTradingConfigured() || !instructionPairQuery || !isTradingSessionReady || !user) return;
    setInstructionsLoading(true);
    setInstructionsError(null);
    try {
      const params = {
        ...instructionPairQuery,
        action: instructionActionFilter === 'all' ? undefined : instructionActionFilter,
      };
      const rows = force ? await listInstructions(params) : await listInstructionsIfIdle(params);
      if (rows === null) return;
      setInstructions(rows);
    } catch (e) {
      setInstructionsError(e instanceof Error ? e.message : String(e));
      setInstructions([]);
    } finally {
      setInstructionsLoading(false);
    }
  }, [instructionPairQuery, instructionActionFilter, isTradingSessionReady, user]);

  useEffect(() => {
    if (panelTab !== 'orders' || !isManualTradingConfigured()) return;
    void refreshInFlightTasks();
  }, [panelTab, refreshInFlightTasks]);

  useEffect(() => {
    if (panelTab !== 'instructions' || !isManualTradingConfigured() || !isTradingSessionReady || !user) return;
    if (!instructionPairQuery) {
      setInstructions([]);
      setInstructionsError(legs.ok ? null : legs.error);
      return;
    }
    void refreshInstructions();
  }, [panelTab, refreshInstructions, instructionPairQuery, legs, isTradingSessionReady, user]);

  const onCancelInstruction = useCallback(
    async (instructionId: string) => {
      clearFeedback();
      if (!isManualTradingConfigured()) {
        setError('请在 .env 中配置 VITE_MANUAL_TRADING_ORIGIN');
        return;
      }
      setCancellingId(instructionId);
      try {
        await cancelInstruction(instructionId);
        setMessage(`已取消指令 ${instructionId.slice(0, 8)}…`);
        if (editingInstruction?.instruction_id === instructionId) {
          setEditingInstruction(null);
        }
        await refreshInstructions(true);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setCancellingId(null);
      }
    },
    [refreshInstructions, editingInstruction]
  );

  const onPauseInstruction = useCallback(
    async (instructionId: string) => {
      clearFeedback();
      if (!isManualTradingConfigured()) {
        setError('请在 .env 中配置 VITE_MANUAL_TRADING_ORIGIN');
        return;
      }
      setPausingId(instructionId);
      try {
        await pauseInstruction(instructionId);
        setMessage(`已暂停指令 ${instructionId.slice(0, 8)}…`);
        await refreshInstructions(true);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setPausingId(null);
      }
    },
    [refreshInstructions],
  );

  const onResumeInstruction = useCallback(
    async (instructionId: string) => {
      clearFeedback();
      if (!isManualTradingConfigured()) {
        setError('请在 .env 中配置 VITE_MANUAL_TRADING_ORIGIN');
        return;
      }
      setResumingId(instructionId);
      try {
        await resumeInstruction(instructionId);
        setMessage(`已恢复指令 ${instructionId.slice(0, 8)}…`);
        await refreshInstructions(true);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setResumingId(null);
      }
    },
    [refreshInstructions],
  );

  const onRemoveInFlightTask = useCallback(
    async (task: TaskView) => {
      clearFeedback();
      if (!isManualTradingConfigured()) {
        setError('请在 .env 中配置 VITE_MANUAL_TRADING_ORIGIN');
        return;
      }
      if (!window.confirm(`确认删除在途任务 ${task.symbol}（${task.task_id.slice(0, 8)}…）？`)) {
        return;
      }
      setDeletingTaskId(task.task_id);
      try {
        await removeInFlightTask(taskKeyBodyFromTaskView(task));
        setMessage(`已删除在途任务 ${task.symbol}`);
        await refreshInFlightTasks();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setDeletingTaskId(null);
      }
    },
    [refreshInFlightTasks]
  );

  const onSaveInstructionEdit = useCallback(
    async (draft: InstructionEditDraft) => {
      if (!editingInstruction) return;
      clearFeedback();
      if (!isManualTradingConfigured()) {
        setError('请在 .env 中配置 VITE_MANUAL_TRADING_ORIGIN');
        return;
      }
      const isPersistentClose = isPersistentCloseInstruction(editingInstruction);
      const perOrder = Number(draft.perOrderNotional.trim());
      const minIv = Number(draft.minIntervalMs.trim());
      let target: number | undefined;
      if (!isPersistentClose) {
        target = Number(draft.targetNotional.trim());
        if (!(target > 0) || !Number.isFinite(target)) {
          setError('目标名义须为有效正数');
          return;
        }
      }
      if (!(perOrder > 0) || !Number.isFinite(perOrder)) {
        setError('单次名义须为有效正数');
        return;
      }
      if (!Number.isInteger(minIv) || minIv < 0) {
        setError('下单间隔须为非负整数');
        return;
      }
      let threshold: number | null = null;
      if (draft.thresholdMode !== 'Immediate') {
        threshold = Number(draft.threshold.trim());
        if (!Number.isFinite(threshold)) {
          setError('价差阈值须为有效数字');
          return;
        }
      }
      const instructionId = editingInstruction.instruction_id;
      setUpdatingId(instructionId);
      try {
        await updateInstruction(instructionId, {
          ...(target != null ? { target_notional: target } : {}),
          per_order_notional: perOrder,
          threshold_mode: draft.thresholdMode,
          threshold,
          min_order_interval_ms: minIv,
        });
        notifyInstructionsChanged();
        setMessage(`已更新指令 ${instructionId.slice(0, 8)}…`);
        setEditingInstruction(null);
        await refreshInstructions(true);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setUpdatingId(null);
      }
    },
    [editingInstruction, refreshInstructions]
  );

  if (!isManualTradingConfigured()) {
    return (
      <aside className="trading-engine-panel">
        <h3>手动套利</h3>
        <p className="hint">
          设置 <code>VITE_MANUAL_TRADING_ORIGIN</code>：生产可为 <code>https://arbx.ai/api/trading-engine</code>（同域 HTTPS）；本地开发使用 <code>/api/trading-engine</code>（Vite 代理）。
        </p>
      </aside>
    );
  }

  const isHeightBound = panelHeight != null && panelHeight > 0;
  const panelStyle = isHeightBound ? { height: `${panelHeight}px` } : undefined;

  return (
    <aside
      className={`trading-engine-panel${isHeightBound ? ' is-height-bound' : ''}`}
      style={panelStyle}
    >
      <div className="trading-engine-panel-head">
      <div className="trading-engine-tabs trading-engine-tabs-primary" role="tablist" aria-label="手动交易">
        <button
          type="button"
          role="tab"
          aria-selected={panelTab === 'trade'}
          className={panelTab === 'trade' ? 'is-active' : ''}
          onClick={() => setPanelTab('trade')}
        >
          开平仓
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={panelTab === 'instructions'}
          className={panelTab === 'instructions' ? 'is-active' : ''}
          onClick={() => setPanelTab('instructions')}
        >
          活跃指令
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={panelTab === 'orders'}
          className={panelTab === 'orders' ? 'is-active' : ''}
          onClick={() => setPanelTab('orders')}
        >
          在途订单
        </button>
      </div>
      </div>

      <div className="trading-engine-tab-body">
      {panelTab === 'trade' ? (
        <>
          {tradeContext ? (
            <div className="trading-engine-trade-context">
              <div className="trading-engine-trade-context-head">
                <span className="trading-engine-trade-context-symbol">{tradeContext.symbol}</span>
                <span
                  className="trading-engine-trade-context-direction"
                  title={tradeContext.directionDetail}
                >
                  {tradeContext.directionCode}
                </span>
                <span className="trading-engine-trade-context-direction-detail">
                  {tradeContext.directionDetail}
                </span>
              </div>
              <div className="trading-engine-trade-context-legs">
                <div className="trading-engine-trade-context-leg is-long">
                  <span className="trading-engine-trade-context-leg-side">Long</span>
                  <span className="trading-engine-trade-context-leg-exchange">{tradeContext.longExchange}</span>
                  <span className="trading-engine-trade-context-leg-inst">{tradeContext.longInst}</span>
                </div>
                <div className="trading-engine-trade-context-leg is-short">
                  <span className="trading-engine-trade-context-leg-side">Short</span>
                  <span className="trading-engine-trade-context-leg-exchange">{tradeContext.shortExchange}</span>
                  <span className="trading-engine-trade-context-leg-inst">{tradeContext.shortInst}</span>
                </div>
              </div>
            </div>
          ) : null}

          {!legs.ok ? <p className="error">{legs.error}</p> : null}

          <div className="trading-engine-trade-subtabs" role="tablist" aria-label="开仓关仓">
            <button
              type="button"
              role="tab"
              aria-selected={tradeSubTab === 'open'}
              className={tradeSubTab === 'open' ? 'is-active' : ''}
              onClick={() => {
                setTradeSubTab('open');
                setSingleLegAction('Open');
              }}
            >
              开仓
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tradeSubTab === 'close'}
              className={tradeSubTab === 'close' ? 'is-active' : ''}
              onClick={() => {
                setTradeSubTab('close');
                setSingleLegAction('Close');
              }}
            >
              关仓
            </button>
          </div>

          <div className="trading-engine-leg-mode" role="radiogroup" aria-label="下单模式">
            <label className="trading-engine-leg-mode-option">
              <input
                type="radio"
                name="leg-mode"
                value="hedge"
                checked={legMode === 'hedge'}
                onChange={() => setLegMode('hedge')}
              />
              <span>双腿</span>
            </label>
            <label className="trading-engine-leg-mode-option">
              <input
                type="radio"
                name="leg-mode"
                value="singleLeg"
                checked={legMode === 'singleLeg'}
                onChange={() => setLegMode('singleLeg')}
              />
              <span>单腿</span>
            </label>
          </div>

          {tradeSubTab === 'open' && legMode === 'hedge' ? (
          <fieldset>
            <p className="hint">开仓指令固定为单次有效：累计执行达到目标名义后结束。</p>
            <label>
              触发模式
              <select
                value={openMode}
                onChange={(e) => setOpenMode(e.target.value as OpenInstructionBody['threshold_mode'])}
              >
                <option value="GreaterOrEqual">价差 ≥ 阈值（GreaterOrEqual）</option>
                <option value="Immediate">立即触发（Immediate）</option>
              </select>
            </label>
            {openMode !== 'Immediate' ? (
              <label>
                开仓价差阈值 threshold（相对 long ask）
                <input value={spreadThreshold} onChange={(e) => setSpreadThreshold(e.target.value)} />
              </label>
            ) : null}
            <label>
              目标名义 target_notional (USD)
              <input value={totalNotional} onChange={(e) => setTotalNotional(e.target.value)} />
            </label>
            <label>
              单笔名义 per_order_notional (USD)
              <input value={perOrderNotional} onChange={(e) => setPerOrderNotional(e.target.value)} />
            </label>
            <label>
              最短下单间隔 min_order_interval_ms
              <input value={minIntervalMs} onChange={(e) => setMinIntervalMs(e.target.value)} />
            </label>

            <button type="button" className="btn-open" disabled={busy || !legs.ok} onClick={() => void onSubmitOpen()}>
              {busy ? '提交中…' : '提交开仓指令'}
            </button>
          </fieldset>
          ) : null}

          {tradeSubTab === 'open' && legMode === 'singleLeg' ? (
          <fieldset>
            <p className="hint">
              不经 instruction，直接向 runtime 提交单腿市价 task。价格优先用 L1 盘口，其次用同步仓位标记价。
            </p>
            <label>
              交易所
              <select
                value={singleLegExchange}
                onChange={(e) => setSingleLegExchange(e.target.value as '1' | '2')}
              >
                <option value="1">
                  {exchange1} ({exchange1InstId || '—'})
                </option>
                <option value="2">
                  {exchange2} ({exchange2InstId || '—'})
                </option>
              </select>
            </label>
            <label>
              方向
              <select
                value={singleLegSide}
                onChange={(e) => setSingleLegSide(e.target.value as 'Long' | 'Short')}
              >
                <option value="Long">Long</option>
                <option value="Short">Short</option>
              </select>
            </label>
            <label>
              数量单位
              <select
                value={singleLegSizeMode}
                onChange={(e) => setSingleLegSizeMode(e.target.value as 'token' | 'usd')}
              >
                <option value="token">Token 数量</option>
                <option value="usd">USD 名义</option>
              </select>
            </label>
            <label>
              {singleLegSizeMode === 'usd' ? '名义 (USD)' : 'Token 数量（非 contract size）'}
              <input
                value={singleLegSizeMode === 'usd' ? singleLegNotional : singleLegTokenSize}
                onChange={(e) => {
                  if (singleLegSizeMode === 'usd') {
                    setSingleLegNotional(e.target.value);
                  } else {
                    setSingleLegTokenSize(e.target.value);
                  }
                }}
              />
            </label>
            <button
              type="button"
              className="btn-open"
              disabled={busy || !legs.ok}
              onClick={() => void onSubmitSingleLeg()}
            >
              {busy ? '提交中…' : '提交单腿 task'}
            </button>
          </fieldset>
          ) : null}

          {tradeSubTab === 'close' && legMode === 'hedge' ? (
          <fieldset>
            <p className="hint">
              单次关仓（one_shot）需存在对应方向的开仓指令或对冲仓位；可指定总关仓 token 或 USD 名义。永久关仓（persistent）持续监听价差，每次触发执行固定名义，直至手动取消。
            </p>
            <label>
              指令有效期 validity
              <select
                value={closeValidity}
                onChange={(e) => setCloseValidity(e.target.value as InstructionValidityView)}
              >
                <option value="one_shot">单次有效（one_shot）</option>
                <option value="persistent">永久有效（persistent）</option>
              </select>
            </label>
            <label>
              触发模式
              <select value={closeMode} onChange={(e) => setCloseMode(e.target.value as 'Immediate' | 'SpreadThreshold')}>
                <option value="Immediate">立即触发（Immediate）</option>
                <option value="SpreadThreshold">价差 ≤ 阈值（LessOrEqual）</option>
              </select>
            </label>
            {closeMode === 'SpreadThreshold' ? (
              <label>
                关仓价差阈值 threshold（相对 short ask）
                <input value={closeSpreadThreshold} onChange={(e) => setCloseSpreadThreshold(e.target.value)} />
              </label>
            ) : null}
            {closeValidity === 'one_shot' ? (
              <>
                <label>
                  总关仓数量类型
                  <select
                    value={closeSizeMode}
                    onChange={(e) => setCloseSizeMode(e.target.value as 'token' | 'usd')}
                  >
                    <option value="usd">USD 名义</option>
                    <option value="token">Token 数量</option>
                  </select>
                </label>
                <label>
                  {closeSizeMode === 'usd' ? '总关仓 USD 名义' : '总关仓 token 数量'}
                  <input value={closeTargetAmount} onChange={(e) => setCloseTargetAmount(e.target.value)} />
                </label>
              </>
            ) : null}
            <label>
              单次关仓名义 (USD)
              <input value={closePerOrderNotional} onChange={(e) => setClosePerOrderNotional(e.target.value)} />
            </label>
            <button type="button" className="btn-close" disabled={busy || !legs.ok} onClick={() => void onSubmitClose()}>
              {busy ? '提交中…' : '提交平仓指令'}
            </button>
          </fieldset>
          ) : null}

          {tradeSubTab === 'close' && legMode === 'singleLeg' ? (
          <fieldset>
            <p className="hint">
              不经 instruction，直接向 runtime 提交单腿市价 task。价格优先用 L1 盘口，其次用同步仓位标记价。
            </p>
            <label>
              交易所
              <select
                value={singleLegExchange}
                onChange={(e) => setSingleLegExchange(e.target.value as '1' | '2')}
              >
                <option value="1">
                  {exchange1} ({exchange1InstId || '—'})
                </option>
                <option value="2">
                  {exchange2} ({exchange2InstId || '—'})
                </option>
              </select>
            </label>
            <label>
              方向
              <select
                value={singleLegSide}
                onChange={(e) => setSingleLegSide(e.target.value as 'Long' | 'Short')}
              >
                <option value="Long">Long</option>
                <option value="Short">Short</option>
              </select>
            </label>
            <label>
              数量单位
              <select
                value={singleLegSizeMode}
                onChange={(e) => setSingleLegSizeMode(e.target.value as 'token' | 'usd')}
              >
                <option value="token">Token 数量</option>
                <option value="usd">USD 名义</option>
              </select>
            </label>
            <label>
              {singleLegSizeMode === 'usd' ? '名义 (USD)' : 'Token 数量（非 contract size）'}
              <input
                value={singleLegSizeMode === 'usd' ? singleLegNotional : singleLegTokenSize}
                onChange={(e) => {
                  if (singleLegSizeMode === 'usd') {
                    setSingleLegNotional(e.target.value);
                  } else {
                    setSingleLegTokenSize(e.target.value);
                  }
                }}
              />
            </label>
            <button
              type="button"
              className="btn-close"
              disabled={busy || !legs.ok}
              onClick={() => void onSubmitSingleLeg()}
            >
              {busy ? '提交中…' : '提交单腿 task'}
            </button>
          </fieldset>
          ) : null}
        </>
      ) : panelTab === 'instructions' ? (
        <>
          <p className="hint">
            展示当前页 <strong>{pairContextLabel || '—'}</strong> 的全部活跃开仓/关仓指令（Running / Paused，含两个 spread 方向）。切换币种或交易所对后自动重新查询。
          </p>
          {!legs.ok ? <p className="error">{legs.error}</p> : null}
          <div className="trading-engine-orders-toolbar trading-engine-instructions-filters">
            <label className="trading-engine-filter-field">
              类型
              <select
                value={instructionActionFilter}
                disabled={instructionsLoading || !instructionPairQuery}
                onChange={(e) => setInstructionActionFilter(e.target.value as InstructionActionFilter)}
              >
                <option value="all">全部</option>
                <option value="Open">仅开仓</option>
                <option value="Close">仅关仓</option>
              </select>
            </label>
            <button
              type="button"
              className="btn-secondary"
              disabled={instructionsLoading || !instructionPairQuery}
              onClick={() => void refreshInstructions(true)}
            >
              {instructionsLoading ? '刷新中…' : '刷新'}
            </button>
          </div>
          {editingInstruction ? (
            <InstructionEditForm
              row={editingInstruction}
              busy={updatingId === editingInstruction.instruction_id}
              onCancel={() => setEditingInstruction(null)}
              onSave={(draft) => void onSaveInstructionEdit(draft)}
            />
          ) : null}
          {instructionsError ? <p className="error">{instructionsError}</p> : null}
          {instructions.length === 0 && !instructionsLoading && !instructionsError && instructionPairQuery ? (
            <p className="hint">当前交易对暂无活跃指令。</p>
          ) : null}
          {groupedInstructions.length > 0 ? (
            <div className="trading-engine-instruction-groups">
              {groupedInstructions.map(([sym, rows]) => {
                const openCount = rows.filter((row) => row.action === 'Open').length;
                const closeCount = rows.filter((row) => row.action === 'Close').length;
                return (
                  <section key={sym} className="trading-engine-instruction-group">
                    <header className="trading-engine-instruction-group-header">
                      <span className="trading-engine-instruction-group-symbol">{sym}</span>
                      <span className="trading-engine-instruction-group-counts">
                        {openCount > 0 ? `${openCount} 开仓` : null}
                        {openCount > 0 && closeCount > 0 ? ' · ' : null}
                        {closeCount > 0 ? `${closeCount} 关仓` : null}
                      </span>
                    </header>
                    <div className="trading-engine-orders-wrap">
                      <table className="trading-engine-orders-table trading-engine-instructions-table">
                        <thead>
                          <tr>
                            <th>方向</th>
                            <th>类型</th>
                            <th>名义 (USD)</th>
                            <th>数量 (size)</th>
                            <th>阈值</th>
                            <th>状态</th>
                            <th>操作</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row) => (
                            <tr key={row.instruction_id}>
                              <td>
                                <InstructionLegDirection row={row} />
                              </td>
                              <td>
                                {row.action === 'Close'
                                  ? `关仓 · ${formatInstructionValidity(row.validity)} · ${formatThresholdMode(row.threshold_mode)}`
                                  : `开仓 · ${formatThresholdMode(row.threshold_mode)}`}
                              </td>
                              <td
                                className={`num${isInstructionProgressNa(formatInstructionNotionalProgress(row)) ? ' instruction-progress-na' : ''}`}
                                title={instructionNotionalProgressTitle(row)}
                              >
                                {formatInstructionNotionalProgress(row)}
                              </td>
                              <td
                                className={`num${isInstructionProgressNa(formatInstructionSizeProgress(row)) ? ' instruction-progress-na' : ''}`}
                                title={instructionSizeProgressTitle(row)}
                              >
                                {formatInstructionSizeProgress(row)}
                              </td>
                              <td className="num">{row.threshold ?? '—'}</td>
                              <td>{row.status}</td>
                              <td>
                                <div className="trading-engine-instruction-actions">
                                  {row.status === 'Running' ? (
                                    <InstructionActionIconButton
                                      variant="pause"
                                      label={`暂停指令 ${row.instruction_id}`}
                                      busy={pausingId === row.instruction_id}
                                      disabled={
                                        pausingId === row.instruction_id ||
                                        resumingId === row.instruction_id ||
                                        cancellingId === row.instruction_id ||
                                        updatingId === row.instruction_id ||
                                        (editingInstruction != null &&
                                          editingInstruction.instruction_id !== row.instruction_id)
                                      }
                                      onClick={() => void onPauseInstruction(row.instruction_id)}
                                    />
                                  ) : null}
                                  {row.status === 'Paused' ? (
                                    <InstructionActionIconButton
                                      variant="resume"
                                      label={`恢复指令 ${row.instruction_id}`}
                                      busy={resumingId === row.instruction_id}
                                      disabled={
                                        pausingId === row.instruction_id ||
                                        resumingId === row.instruction_id ||
                                        cancellingId === row.instruction_id ||
                                        updatingId === row.instruction_id ||
                                        (editingInstruction != null &&
                                          editingInstruction.instruction_id !== row.instruction_id)
                                      }
                                      onClick={() => void onResumeInstruction(row.instruction_id)}
                                    />
                                  ) : null}
                                  <InstructionActionIconButton
                                    variant="edit"
                                    label={
                                      editingInstruction?.instruction_id === row.instruction_id
                                        ? `编辑中 ${row.instruction_id}`
                                        : `修改指令 ${row.instruction_id}`
                                    }
                                    disabled={
                                      pausingId === row.instruction_id ||
                                      resumingId === row.instruction_id ||
                                      cancellingId === row.instruction_id ||
                                      updatingId === row.instruction_id ||
                                      (editingInstruction != null &&
                                        editingInstruction.instruction_id !== row.instruction_id)
                                    }
                                    onClick={() => {
                                      clearFeedback();
                                      setEditingInstruction(row);
                                    }}
                                  />
                                  <InstructionActionIconButton
                                    variant="cancel"
                                    label={`取消指令 ${row.instruction_id}`}
                                    busy={cancellingId === row.instruction_id}
                                    disabled={
                                      pausingId === row.instruction_id ||
                                      resumingId === row.instruction_id ||
                                      cancellingId === row.instruction_id ||
                                      updatingId === row.instruction_id
                                    }
                                    onClick={() => void onCancelInstruction(row.instruction_id)}
                                  />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                );
              })}
            </div>
          ) : null}
        </>
      ) : (
        <>
          <p className="hint">
            展示当前用户在服务端 <code>TaskManager</code> 中的在途执行任务（双腿尚未全部终态）。服务重启后列表清空。
          </p>
          <InFlightTaskTable
            tasks={inFlightTasks}
            loading={inFlightLoading}
            error={inFlightError}
            emptyHint="暂无在途任务。"
            finishedAtLabel="创建(UTC)"
            variant="in-flight"
            onRefresh={refreshInFlightTasks}
            onDelete={onRemoveInFlightTask}
            deletingTaskId={deletingTaskId}
          />
        </>
      )}
      </div>

      <div className="trading-engine-panel-foot">
        {error ? <p className="error">{error}</p> : null}
        {message ? <p className="ok">{message}</p> : null}
      </div>
    </aside>
  );
}
